import React, { useState, useEffect, useCallback, useMemo } from "react";
import CustomerListContainer from "../CustomerListContainer/CustomerListContainer";
import { MapContainer, FeatureGroup, useMap } from "react-leaflet";
import { TileLayerWithHeader } from "../../components/MapContentComponent/TileLayerWithHeader";
import { useMsal } from "Auth/msal-context";
import CustomerMarker from "../../components/MapContentComponent/CustomerMarker";
import config from "config";
import { useCustomersList, useSelectedMapSystems } from '../../hooks/useCloudSelectors';
import { Box, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const DEFAULT_CENTER = [62.93305059673888, 27.65178677914856];
const DEFAULT_ZOOM = 6;
const SELECTED_ZOOM = 7;

const SetViewOnClick = ({ coords, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(coords, zoom);
  }, [coords, zoom]);
  return null;
};

const MapViewContainer = () => {
  const { acquireToken, user } = useMsal();
  const customersList = useCustomersList();
  const selectedMapSystems = useSelectedMapSystems();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [token, setToken] = useState();
  const [position, setPosition] = useState(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [showSidebar, setShowSidebar] = useState(true);

  // Pan map when selected systems change
  useEffect(() => {
    if (selectedMapSystems.length === 0) {
      setPosition(DEFAULT_CENTER);
      setMapZoom(DEFAULT_ZOOM);
      return;
    }
    const foundCustomer = customersList.find((customer) =>
      customer.devices.some((device) =>
        selectedMapSystems.some((sel) => sel.deviceId === device.deviceId)
      )
    );
    if (foundCustomer) {
      setPosition([foundCustomer.gpsLat + 1.1, foundCustomer.gpsLong]);
      setMapZoom(SELECTED_ZOOM);
    }
  }, [selectedMapSystems]);

  // Acquire Azure Maps token
  useEffect(() => {
    if (!user) return;
    acquireToken({
      scopes: ["https://atlas.microsoft.com/user_impersonation"],
      forceRefresh: false,
      account: user,
    })
      .then((response) => setToken(response.accessToken))
      .catch((error) => console.error("Map token error:", error));
  }, [user]);

  const visibleCustomers = useMemo(
    () => customersList.filter((item) => item.onMap),
    [customersList]
  );

  console.log("visibleCustomers", visibleCustomers);

  return (
    <Box sx={{ height: "100%", width: "100%", display: "flex", borderTop: "1px solid", borderColor: "primary.main", overflow: "hidden" }}>
      {/* Sidebar (desktop only) */}
      {!isMobile && (
        <Box
          sx={{
            width: showSidebar ? { lg: 350, xl: 370 } : 0,
            minWidth: showSidebar ? { lg: 350, xl: 370 } : 0,
            transition: "width 0.25s ease, min-width 0.25s ease",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "background.dark",
            borderRight: showSidebar ? "1px solid" : "none",
            borderColor: "primary.main",
          }}
        >
          <Box sx={{ overflowY: "auto", flex: 1 }}>
            <CustomerListContainer selectedSystems={selectedMapSystems} />
          </Box>
        </Box>
      )}

      {/* Sidebar toggle button */}
      {!isMobile && (
        <Tooltip title={showSidebar ? "Hide sites" : "Show sites"} placement="right">
          <IconButton
            onClick={() => setShowSidebar((prev) => !prev)}
            size="small"
            sx={{
              position: "absolute",
              left: showSidebar ? { lg: 350, xl: 370 } : 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1000,
              bgcolor: "background.light",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "0 4px 4px 0",
              transition: "left 0.25s ease",
              "&:hover": { bgcolor: "action.hover" },
              width: 24,
              height: 48,
            }}
          >
            {showSidebar ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}

      {/* Map */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {token && (
          <MapContainer
            center={position}
            zoom={mapZoom}
            style={{ width: "100%", height: "100%" }}
          >
            <SetViewOnClick coords={position} zoom={mapZoom} />
            <TileLayerWithHeader
              url="https://atlas.microsoft.com/map/tile?api-version=2.0&tilesetId={tilesetId}&zoom={z}&x={x}&y={y}&language={language}&view={view}"
              tilesetId="microsoft.base.darkgrey"
              language="fi-FI"
              view="Auto"
              attribution={`\u00A9 ${new Date().getFullYear()} TomTom, Microsoft`}
              headers={[{ Authorization: `Bearer ${token}`, "X-MS-CLIENT-ID": config.MAP_CLIENT_ID }]}
            />
            <FeatureGroup>
              {visibleCustomers.map((cus) => (
                <CustomerMarker customer={cus} key={cus.customerName} />
              ))}
            </FeatureGroup>
          </MapContainer>
        )}
      </Box>
    </Box>
  );
};

export default MapViewContainer;